/**
 * Age bands for the stats sheet.
 *
 * These are NOT the bands in `person_projection.age_range`, which are decade-ish
 * (`under 45`, `45-54`, `55-64`, `65-74`, `75+`) and exist to carry whatever
 * Oryx already wrote in `person.age_range`. These are five-year bands around the
 * presbyopia onset window, which is the population Oryx actually trains, so the
 * interesting spread — the low fifties — does not collapse into one bar.
 *
 * Kept here, pure, for the same reason the stage rules are: one definition,
 * unit-tested in milliseconds, and never re-expressed as SQL where it would
 * drift from the tested version.
 */
export const AGE_BANDS = [
  { id: 'under40', label: 'Under 40', min: null, max: 39 },
  { id: '40_45', label: '40–45', min: 40, max: 45 },
  { id: '46_50', label: '46–50', min: 46, max: 50 },
  { id: '51_55', label: '51–55', min: 51, max: 55 },
  { id: '56_60', label: '56–60', min: 56, max: 60 },
  { id: '61_65', label: '61–65', min: 61, max: 65 },
  { id: '66plus', label: '66+', min: 66, max: null },
] as const

export type AgeBand = (typeof AGE_BANDS)[number]
export type AgeBandId = AgeBand['id']

/**
 * The band an age falls in, or null when the age is unknown or not a usable
 * number. A negative age is a data error, not an "under 40" — it is reported as
 * unknown so it shows up in the unknown count rather than hiding in a bar.
 */
export function ageBandFor(age: number | null | undefined): AgeBandId | null {
  if (age == null || !Number.isFinite(age) || age < 0) return null
  const years = Math.floor(age)
  for (const band of AGE_BANDS) {
    if (band.max == null || years <= band.max) return band.id
  }
  return null
}

export interface AgeTally {
  /** One entry per band, in band order, including bands with no one in them. */
  bands: Array<{ id: AgeBandId; label: string; count: number }>
  /** People whose age is known and landed in a band. */
  known: number
  /** People counted but with no usable date of birth. */
  unknown: number
  /** Median of the known ages, or null when nothing is known. */
  medianAge: number | null
}

/**
 * Tally people into bands from `(age, count)` pairs — the shape a `group by age`
 * returns. Rows with a null age are the unknown count.
 */
export function tallyAgeBands(rows: Iterable<{ age: number | null; count: number }>): AgeTally {
  const counts = new Map<AgeBandId, number>(AGE_BANDS.map((b) => [b.id, 0]))
  const knownAges: Array<{ age: number; count: number }> = []
  let known = 0
  let unknown = 0

  for (const row of rows) {
    const n = Number.isFinite(row.count) ? Math.max(0, Math.trunc(row.count)) : 0
    if (n === 0) continue
    const band = ageBandFor(row.age)
    if (band == null) {
      unknown += n
      continue
    }
    counts.set(band, counts.get(band)! + n)
    knownAges.push({ age: Math.floor(row.age!), count: n })
    known += n
  }

  return {
    bands: AGE_BANDS.map((b) => ({ id: b.id, label: b.label, count: counts.get(b.id)! })),
    known,
    unknown,
    medianAge: median(knownAges, known),
  }
}

/** Median over a weighted list, without expanding it into one entry per person. */
function median(weighted: Array<{ age: number; count: number }>, total: number): number | null {
  if (total === 0) return null
  const sorted = [...weighted].sort((a, b) => a.age - b.age)
  // The two middle people coincide when the total is odd, and the average of
  // the pair is the median when it is even — one expression covers both.
  const lower = nth(sorted, Math.floor((total - 1) / 2))
  const upper = nth(sorted, Math.floor(total / 2))
  return (lower + upper) / 2
}

/** The age of the nth person (0-based) in an age-sorted weighted list. */
function nth(sorted: Array<{ age: number; count: number }>, index: number): number {
  let seen = 0
  for (const entry of sorted) {
    seen += entry.count
    if (index < seen) return entry.age
  }
  return sorted[sorted.length - 1]!.age
}
